// src/services/authBridge.ts
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth';

import { auth } from '../lib/firebase';

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(
      () => reject(new Error(`${label}_timeout_after_${ms}ms`)),
      ms,
    );

    p.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(t);
        reject(e);
      },
    );
  });
}

function nowId(prefix: string) {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

async function waitForJsUidMatch(expectedUid: string, ms: number): Promise<boolean> {
  if (auth.currentUser?.uid === expectedUid) return true;

  return await new Promise<boolean>((resolve) => {
    let done = false;

    const t = window.setTimeout(() => {
      if (done) return;
      done = true;
      try {
        unsub();
      } catch {}
      resolve(auth.currentUser?.uid === expectedUid);
    }, ms);

    const unsub = onAuthStateChanged(auth, (u) => {
      if (done) return;

      if (u?.uid === expectedUid) {
        done = true;
        window.clearTimeout(t);
        try {
          unsub();
        } catch {}
        resolve(true);
      }
    });
  });
}

export function getAuthBridgeDiag() {
  const anyAuth: any = auth as any;

  return {
    isNative: isNative(),
    jsUid: auth.currentUser?.uid ?? null,
    projectId:
      anyAuth?.app?.options?.projectId ||
      anyAuth?._app?.options?.projectId ||
      'roddo-official',
    authDomain:
      anyAuth?.app?.options?.authDomain ||
      anyAuth?._app?.options?.authDomain ||
      null,
  };
}

function getProjectId(): string {
  const diag = getAuthBridgeDiag();
  return diag.projectId || 'roddo-official';
}

function getAuthBridgeHttpUrl(): string {
  return `https://us-central1-${getProjectId()}.cloudfunctions.net/authBridgeCustomTokenHttp`;
}

function extractToken(value: any): string | null {
  if (!value) return null;

  if (typeof value === 'string') return value;

  return (
    value?.token ||
    value?.idToken ||
    value?.result?.token ||
    value?.result?.idToken ||
    value?.credential?.idToken ||
    null
  );
}

async function getNativeFirebaseIdToken(forceRefresh = true): Promise<string | null> {
  if (!isNative()) return null;

  try {
    const res: any = await withTimeout(
      FirebaseAuthentication.getIdToken({ forceRefresh } as any),
      15000,
      'FirebaseAuthentication.getIdToken',
    );

    const token = extractToken(res);

    console.log('[auth-bridge] native getIdToken result ' + JSON.stringify({
      hasToken: !!token,
      keys: res && typeof res === 'object' ? Object.keys(res) : [],
      diag: getAuthBridgeDiag(),
    }));

    return token;
  } catch (e: any) {
    console.warn('[auth-bridge] native getIdToken failed ' + JSON.stringify({
      code: e?.code ?? null,
      name: e?.name ?? null,
      message: e?.message || String(e),
    }));

    return null;
  }
}

async function mintCustomTokenFromNativeIdToken(
  firebaseIdToken: string,
  bridgeId: string,
): Promise<{ customToken: string; uid: string | null }> {
  const url = getAuthBridgeHttpUrl();
  const diagId = nowId('diag');

  const res = await withTimeout(
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        idToken: firebaseIdToken,
        bridgeId,
        diagId,
      }),
    }),
    20000,
    'authBridgeCustomTokenHttp',
  );

  let payload: any = null;

  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    throw new Error(
      `auth_bridge_http_${res.status}_${payload?.error || payload?.message || 'unknown'}`,
    );
  }

  if (!payload?.customToken || typeof payload.customToken !== 'string') {
    throw new Error('auth_bridge_custom_token_missing');
  }

  return {
    customToken: payload.customToken,
    uid: payload?.uid ?? null,
  };
}

async function bridgeJsAuthWithNativeCustomToken(
  expectedNativeUid: string | null,
  reason: string,
): Promise<boolean> {
  const bridgeId = nowId('bridge_native');

  try {
    const nativeRes = await FirebaseAuthentication.getCurrentUser();
    const nativeUid = expectedNativeUid || nativeRes?.user?.uid || null;

    if (!nativeUid) {
      throw new Error('native_user_missing');
    }

    const existingJsUid = auth.currentUser?.uid ?? null;
    if (existingJsUid === nativeUid) {
      lastResult = true;
      return true;
    }

    const firebaseIdToken = await getNativeFirebaseIdToken(true);

    if (!firebaseIdToken) {
      throw new Error('native_firebase_id_token_missing');
    }

    console.log('[auth-bridge] custom token bridge start ' + JSON.stringify({
      reason,
      bridgeId,
      nativeUid,
      existingJsUid,
      url: getAuthBridgeHttpUrl(),
      diag: getAuthBridgeDiag(),
    }));

    const minted = await mintCustomTokenFromNativeIdToken(firebaseIdToken, bridgeId);

    if (minted.uid && minted.uid !== nativeUid) {
      throw new Error(`custom_token_uid_mismatch_${minted.uid}_expected_${nativeUid}`);
    }

    const userCredential = await withTimeout(
      signInWithCustomToken(auth, minted.customToken),
      30000,
      'signInWithCustomToken',
    );

    const signedUid = userCredential.user?.uid ?? auth.currentUser?.uid ?? null;
    const matched = await waitForJsUidMatch(nativeUid, 5000);
    const finalUid = auth.currentUser?.uid ?? null;

    lastResult = matched && finalUid === nativeUid;

    console.log('[auth-bridge] custom token bridge final ' + JSON.stringify({
      reason,
      bridgeId,
      nativeUid,
      signedUid,
      finalUid,
      matched,
      ok: lastResult,
    }));

    return lastResult;
  } catch (e: any) {
    lastResult = false;

    console.warn('[auth-bridge] custom token bridge failed ' + JSON.stringify({
      reason,
      bridgeId,
      code: e?.code ?? null,
      name: e?.name ?? null,
      message: e?.message || String(e),
      stack: e?.stack ?? null,
      diag: getAuthBridgeDiag(),
    }));

    return false;
  }
}

let lastResult: boolean | null = null;
let bridgeResultInFlight: Promise<boolean> | null = null;
let ensureInFlight: Promise<boolean> | null = null;

async function bridgeJsAuthWithGoogleResultInner(
  googleResult: any,
  expectedNativeUid: string | null = null,
  reason: string = 'nativeGoogle',
): Promise<boolean> {
  const bridgeId = nowId('bridge_result');

  const idToken = googleResult?.credential?.idToken;
  const accessToken = googleResult?.credential?.accessToken ?? undefined;
  const resultUserUid = googleResult?.user?.uid ?? null;
  const expectedUid = expectedNativeUid || resultUserUid;

  console.log('[auth-bridge] bridge google result ' + JSON.stringify({
    reason,
    bridgeId,
    expectedUid,
    resultUserUid,
    hasIdToken: !!idToken,
    hasAccessToken: !!accessToken,
    diag: getAuthBridgeDiag(),
  }));

  if (idToken || accessToken) {
    try {
      const credential = GoogleAuthProvider.credential(idToken || undefined, accessToken);

      const userCredential = await withTimeout(
        signInWithCredential(auth, credential),
        30000,
        'signInWithCredential',
      );

      const afterUid = userCredential.user?.uid ?? auth.currentUser?.uid ?? null;

      if (expectedUid) {
        const matched = await waitForJsUidMatch(expectedUid, 5000);
        const finalUid = auth.currentUser?.uid ?? null;
        lastResult = matched && finalUid === expectedUid;

        console.log('[auth-bridge] bridge google result final ' + JSON.stringify({
          reason,
          bridgeId,
          expectedUid,
          finalUid,
          matched,
          ok: lastResult,
        }));

        if (lastResult) return true;
      } else {
        lastResult = !!afterUid;
        if (lastResult) return true;
      }
    } catch (e: any) {
      console.warn('[auth-bridge] direct google credential bridge failed; falling back native token ' + JSON.stringify({
        reason,
        bridgeId,
        code: e?.code ?? null,
        name: e?.name ?? null,
        message: e?.message || String(e),
      }));
    }
  } else {
    console.warn('[auth-bridge] google credential missing; falling back native token ' + JSON.stringify({
      reason,
      bridgeId,
      expectedUid,
      resultUserUid,
    }));
  }

  return await bridgeJsAuthWithNativeCustomToken(expectedUid, `${reason}:fallbackCustomToken`);
}

export async function bridgeJsAuthWithGoogleResult(
  googleResult: any,
  expectedNativeUid: string | null = null,
  reason: string = 'nativeGoogle',
): Promise<boolean> {
  if (bridgeResultInFlight) {
    console.log('[auth-bridge] bridge already in flight', { reason, diag: getAuthBridgeDiag() });
    return bridgeResultInFlight;
  }

  bridgeResultInFlight = bridgeJsAuthWithGoogleResultInner(
    googleResult,
    expectedNativeUid,
    reason,
  );

  try {
    return await bridgeResultInFlight;
  } finally {
    bridgeResultInFlight = null;
  }
}

export async function ensureJsAuthFromNative(reason: string = 'unknown'): Promise<boolean> {
  if (!isNative()) return true;

  if (ensureInFlight) return ensureInFlight;

  ensureInFlight = (async () => {
    const jsUid = auth.currentUser?.uid ?? null;
    const nativeRes = await FirebaseAuthentication.getCurrentUser();
    const nativeUid = nativeRes?.user?.uid ?? null;

    if (!nativeUid) {
      // Email/password and older JS-only sessions can still be valid on native
      // even when the Capacitor plugin has no native Firebase user. Do not turn
      // that into a forced login loop.
      if (jsUid) {
        lastResult = true;
        console.log('[auth-bridge] no native user, but JS auth is already active', {
          reason,
          jsUid,
        });
        return true;
      }

      lastResult = false;
      console.warn('[auth-bridge] no native user', { reason, jsUid });
      return false;
    }

    if (jsUid === nativeUid) {
      lastResult = true;
      console.log('[auth-bridge] already bridged', { reason, nativeUid, jsUid });
      return true;
    }

    console.warn('[auth-bridge] native user exists but JS auth is not bridged; repairing with custom token', {
      reason,
      nativeUid,
      jsUid,
      diag: getAuthBridgeDiag(),
    });

    return await bridgeJsAuthWithNativeCustomToken(nativeUid, reason);
  })();

  try {
    return await ensureInFlight;
  } finally {
    ensureInFlight = null;
  }
}

export async function signOutEverywhere(): Promise<void> {
  try {
    if (isNative()) await FirebaseAuthentication.signOut();
  } catch {}

  try {
    await signOut(auth);
  } catch {}

  lastResult = null;
}

export function getLastAuthBridgeResult(): boolean | null {
  return lastResult;
}

export async function runNativeAuthBridgeNetDiag(): Promise<{
  ok: boolean;
  isNative: boolean;
  jsUid: string | null;
  nativeUid: string | null;
  message: string;
}> {
  try {
    const nativeRes = isNative()
      ? await FirebaseAuthentication.getCurrentUser()
      : null;

    const nativeUid = nativeRes?.user?.uid ?? null;
    const jsUid = auth.currentUser?.uid ?? null;

    if (nativeUid && jsUid !== nativeUid) {
      const repaired = await ensureJsAuthFromNative('netdiag');

      return {
        ok: repaired && auth.currentUser?.uid === nativeUid,
        isNative: isNative(),
        jsUid: auth.currentUser?.uid ?? null,
        nativeUid,
        message:
          repaired && auth.currentUser?.uid === nativeUid
            ? 'Native and JS auth repaired.'
            : 'Native and JS auth do not match.',
      };
    }

    const result = {
      ok: !nativeUid || jsUid === nativeUid,
      isNative: isNative(),
      jsUid,
      nativeUid,
      message:
        !nativeUid
          ? 'No native user.'
          : jsUid === nativeUid
            ? 'Native and JS auth match.'
            : 'Native and JS auth do not match.',
    };

    console.log('[auth-bridge][netdiag]', result);
    return result;
  } catch (e: any) {
    const result = {
      ok: false,
      isNative: isNative(),
      jsUid: auth.currentUser?.uid ?? null,
      nativeUid: null,
      message: e?.message || String(e),
    };

    console.warn('[auth-bridge][netdiag] failed', result, e);
    return result;
  }
}